import React, { useCallback, useContext, useMemo } from 'react';
import { TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { isNull } from 'lodash';
import { IndividualRequestedFile } from 'api';
import { DocumentsContext } from 'context/DocumentsContext';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import { OptionInterface } from 'dashboard409a/pages/Information/CurrentRequestedDocumentsAndQuestions/types';
import { REFERENCES_DATA } from 'pages/Documents/components/EditDocumentDialog/constants';
import { ReferencesSelectorProps } from './types';

const ReferencesSelector = ({ formValues, setFormValues }: ReferencesSelectorProps) => {
  const documentsContext = useContext(DocumentsContext);
  const informationDashboardContext = useContext(InformationDashboard409AContext);
  // This is because the modal can be called from the Reference Widget, Documents page or Information page (Company Dashboard)
  const { featuresObjects } = documentsContext || informationDashboardContext || {};

  const options = useMemo<OptionInterface[]>(
    () =>
      REFERENCES_DATA.map(reference => {
        const referencedFeatureId = featuresObjects ? featuresObjects[reference.slug] : null;
        const isDisabled = isNull(referencedFeatureId);
        return {
          ...reference,
          objectId: referencedFeatureId,
          isDisabled,
          reference_object_id: referencedFeatureId,
          reference_object_type: reference.type,
        };
      }),
    [featuresObjects]
  );

  const handleReferenceChange = useCallback(
    (__event: React.ChangeEvent<object>, data: OptionInterface | null) => {
      setFormValues({
        ...formValues,
        reference_object_id: data?.objectId ?? null,
        reference_object_type: (data?.type as IndividualRequestedFile['reference_object_type']) ?? null,
      });
    },
    [formValues, setFormValues]
  );

  return (
    <div>
      <Typography variant="body1" gutterBottom style={{ fontWeight: 700, marginTop: '1rem' }}>
        References
      </Typography>
      <Autocomplete
        id="references"
        options={options}
        getOptionDisabled={option => option.isDisabled}
        getOptionLabel={option => option.name}
        getOptionSelected={(option, value) => option.objectId === (value.reference_object_id ?? value.objectId)}
        value={options.find(option => option.objectId === formValues.reference_object_id) || null}
        onChange={handleReferenceChange}
        renderOption={option => <>{option.name}</>}
        style={{ width: '100%' }}
        renderInput={params => <TextField {...params} variant="outlined" placeholder="Select an option" />}
      />
    </div>
  );
};

export default ReferencesSelector;
